import { authFetch } from '@/lib/auth/session';
import { needsArchiveRepair } from '@/lib/consultation-archive-recovery';
import { resolveBackendOrigin } from '@/lib/api/base-url';
import { getLocalGcpUploadProxyEndpoint } from '@/lib/api/local-gcp-upload-routing';
import type { ConsultationCapabilities, ConsultationDocument } from '@/types/consultation';
import { useAuthStore } from '@/stores/authStore';
import { createScopedReadCache } from './scoped-read-cache';
import { getAuthScopeRevision, registerAuthScopedCacheReset } from '../auth-scoped-state-reset';

const PREFIX = '/api/canvas-chat/consultation';

export interface ConsultationLibrarySource {
  kind: 'subject_model' | 'custom_style' | 'public_image';
  source_id: string;
}

export function getConsultationDocumentPreviewUrl(projectId: string, documentId: string): string {
  return `/api/auth/media/canvas-chat/consultation/documents/${encodeURIComponent(documentId)}/preview?project_id=${encodeURIComponent(projectId)}`;
}

const REFERENCE_ERRORS = new Set([
  'asset_not_found', 'asset_not_ready', 'asset_image_required', 'asset_storage_path_invalid',
  'CONSULTATION_DOCUMENT_TOO_LARGE', 'document_size_invalid', 'document_storage_unavailable', 'invalid_image', 'invalid_video',
  'unsupported_document_type', 'CONSULTATION_DISABLED',
  'archive_size_limit_exceeded', 'archive_expanded_limit_exceeded', 'material_document_limit_exceeded',
  'archive_member_encrypted', 'archive_decoder_unavailable', 'archive_path_invalid', 'archive_invalid', 'archive_empty', 'material_visual_limit_exceeded', 'material_read_limit_exceeded',
  'EXPLORATION_SELECTION_LIMIT', 'EXPLORATION_VERSION_LIMIT', 'EXPLORATION_ROUND_LIMIT',
  'REFERENCE_REFRESH_STALE', 'REFERENCE_REFRESH_PENDING', 'attachment_limit',
  'presentation_converter_unavailable', 'presentation_conversion_timeout', 'presentation_conversion_failed',
  'presentation_conversion_size_limit_exceeded', 'invalid_presentation', 'encrypted_document',
  'document_version_mismatch', 'document_not_found', 'document_upload_pending',
]);

export class ConsultationRequestError extends Error {
  constructor(public readonly code: string, public readonly status: number) {
    super('consultation_request_failed');
    this.name = 'ConsultationRequestError';
  }
}

async function readEnvelope<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => null);
  if (!response.ok || body?.success !== true) {
    const rawCode = body?.detail?.code ?? body?.error?.code;
    const code = response.status === 401 ? 'authentication_required'
      : response.status === 403 ? 'project_access_denied'
      : REFERENCE_ERRORS.has(rawCode) ? rawCode : 'reference_prepare_failed';
    throw new ConsultationRequestError(code, response.status);
  }
  return body.data as T;
}

const capabilityReads = createScopedReadCache<ConsultationCapabilities>(30_000);
registerAuthScopedCacheReset('consultation-capabilities', capabilityReads.clear);

export function getConsultationCapabilities(signal?: AbortSignal): Promise<ConsultationCapabilities> {
  // A changed auth user object invalidates in-flight and cached capabilities,
  // including logout/login to the same account. Keep this out of persistent storage.
  return capabilityReads.read(useAuthStore.getState().user, async () => {
    const response = await authFetch(`${resolveBackendOrigin()}${PREFIX}/capabilities`, {
      timeout: 8_000,
      retries: 0,
    });
    return readEnvelope<ConsultationCapabilities>(response);
  }, signal);
}

/** Fixed owned identities may be journaled with the original Blob; never signed URLs. */
export interface ConsultationUploadRecovery {
  intent?: {
    file: File; projectId: string; ownerId: string | null; authRevision: number;
    fingerprint: string; byteSize: number; uploadId: string;
  };
  /** Set only by explicit restoration; mounting must not start this request. */
  resumeSameIdentity?: boolean;
  prepared?: {
    file: File; projectId: string; ownerId: string | null; authRevision: number;
    fingerprint: string; byteSize: number; documentId: string; putState: 'completed' | 'unknown';
  };
}

export interface ConsultationUploadProgress {
  stage: 'hashing' | 'authorizing' | 'uploading' | 'confirming' | 'organizing' | 'ready';
  completedItems?: number;
  totalItems?: number;
  bytesUploaded?: number;
  bytesTotal?: number;
  document?: ConsultationDocument;
}

export async function uploadConsultationDocument(projectId: string, file: File, signal?: AbortSignal,
  recovery: ConsultationUploadRecovery = {},
  onProgress?: (progress: ConsultationUploadProgress) => void,
  onCheckpoint?: () => Promise<void>): Promise<ConsultationDocument> {
  // Container bytes bypass the API ceiling. Keep only this attachment's owned
  // identity after PUT, so a manual retry cannot upload its bytes a second time.
  // Every container needs the same recoverable staging identity and verified
  // canonical copy; a small archive can still be expensive to materialize.
  if (/\.(pptx?|zip|rar)$/i.test(file.name)) {
    const ownerId = useAuthStore.getState().user?.id ?? null;
    const authRevision = getAuthScopeRevision();
    const clear = () => { delete recovery.prepared; delete recovery.intent; };
    const current = () => {
      if (signal?.aborted || getAuthScopeRevision() !== authRevision
        || (useAuthStore.getState().user?.id ?? null) !== ownerId) {
        clear();
        throw new ConsultationRequestError('reference_prepare_failed', 408);
      }
    };
    const previous = recovery.prepared;
    if (previous && (previous.file !== file || previous.projectId !== projectId
      || previous.ownerId !== ownerId || previous.authRevision !== authRevision || previous.byteSize !== file.size)) {
      if (recovery.resumeSameIdentity) throw new ConsultationRequestError('document_version_mismatch', 409);
      clear();
    }
    const intent = recovery.intent;
    if (intent && (intent.file !== file || intent.projectId !== projectId || intent.ownerId !== ownerId
      || intent.authRevision !== authRevision || intent.byteSize !== file.size)) {
      if (recovery.resumeSameIdentity) throw new ConsultationRequestError('document_version_mismatch', 409);
      clear();
    }
    signal?.addEventListener('abort', clear, {once: true});
    try {
      current();
      const query = `?project_id=${encodeURIComponent(projectId)}`;
      const checkpoint = async () => { current(); await onCheckpoint?.(); current(); };
      if (recovery.resumeSameIdentity && (recovery.intent || recovery.prepared)) {
        onProgress?.({stage: 'hashing'});
        const { hashUploadFile } = await import('./upload-content-hash');
        const fingerprint = await hashUploadFile(file, signal);
        current();
        if (fingerprint !== (recovery.intent ?? recovery.prepared)!.fingerprint) {
          throw new ConsultationRequestError('document_version_mismatch', 409);
        }
      }
      if (!recovery.prepared) {
        if (!recovery.intent) {
          onProgress?.({stage: 'hashing'});
          const { hashUploadFile } = await import('./upload-content-hash');
          const fingerprint = await hashUploadFile(file, signal);
          current();
          recovery.intent = {file, projectId, ownerId, authRevision, fingerprint,
            byteSize: file.size, uploadId: crypto.randomUUID()};
          await checkpoint();
        }
        const fingerprint = recovery.intent.fingerprint;
        onProgress?.({stage: 'authorizing'});
        const prepared = await writeConsultationDocument<{document_id: string; upload_url: string; content_type: string}>(
          `/canvas-chat/consultation/documents/presign-upload${query}`, {
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({upload_id: recovery.intent.uploadId, filename: file.name, byte_size: file.size, fingerprint}),
          }, signal,
        );
        current();
        // A reload during PUT retains this same reserved document and upload
        // intent. Unknown acknowledgement is not proof of completed storage.
        recovery.prepared = {file, projectId, ownerId, authRevision, fingerprint, byteSize: file.size,
          documentId: prepared.document_id, putState: 'unknown'};
        await checkpoint();
        const { uploadFileToSignedUrl, SignedUploadError } = await import('./materials');
        let putState: 'completed' | 'unknown' = 'completed';
        try {
          onProgress?.({stage: 'uploading', bytesUploaded: 0, bytesTotal: file.size});
          await uploadFileToSignedUrl(prepared.upload_url, file, prepared.content_type, 1,
            progress => onProgress?.({stage: 'uploading', bytesUploaded: progress.bytesUploaded,
              bytesTotal: progress.bytesTotal}), signal);
        } catch (error) {
          // Only an unknown ACK may continue to confirmation. A definite PUT
          // rejection does not establish a complete object and saves no resume.
          if (signal?.aborted || !(error instanceof SignedUploadError)
            || !['upload_timeout', 'upload_network'].includes(error.code)) {
            delete recovery.prepared;
            if (!signal?.aborted) await checkpoint();
            throw error;
          }
          putState = 'unknown';
        }
        current();
        recovery.prepared = {file, projectId, ownerId, authRevision, fingerprint, byteSize: file.size,
          documentId: prepared.document_id, putState};
        recovery.resumeSameIdentity = false;
        await checkpoint();
      }
      const documentId = recovery.prepared.documentId;
      for (let attempt = 0; ; attempt += 1) {
        current();
        onProgress?.({stage: 'confirming'});
        try {
          const document = await writeConsultationDocument<ConsultationDocument>(
            `/canvas-chat/consultation/documents/confirm-upload${query}`, {
              headers: {'Content-Type': 'application/json'}, body: JSON.stringify({document_id: documentId}),
            }, signal, 300_000,
          );
          current();
          // A successful envelope may still contain a recoverable child storage
          // failure. Keep the verified original and staging checkpoint for an
          // explicit retry; this code must not enter the transient POST loop.
          if (needsArchiveRepair(document)) {
            throw new ConsultationRequestError('archive_members_storage_unavailable', 409);
          }
          clear();
          onProgress?.({stage: 'ready', document});
          return document;
        } catch (error) {
          current();
          const transient = error instanceof ConsultationRequestError
            ? error.code === 'document_upload_pending' || ([408, 502, 503, 504].includes(error.status)
              && ['reference_prepare_failed', 'document_storage_unavailable'].includes(error.code))
            : error instanceof TypeError || (error instanceof Error && error.name === 'TimeoutError');
          if (attempt >= 1 || !transient) {
            if (transient && recovery.resumeSameIdentity && recovery.intent?.uploadId) {
              // This user-initiated continuation may replay the SAME original
              // bytes into the SAME staging identity. 503 is not classified as
              // missing; confirm was attempted first, including a lost final ACK.
              delete recovery.prepared;
              recovery.resumeSameIdentity = false;
              await checkpoint();
              return await uploadConsultationDocument(projectId, file, signal, recovery, onProgress, onCheckpoint);
            }
            throw error;
          }
        }
      }
    } catch (error) {
      if (error instanceof ConsultationRequestError) throw error;
      throw new ConsultationRequestError('reference_prepare_failed', 408);
    } finally {
      signal?.removeEventListener('abort', clear);
    }
  }
  const body = new FormData();
  body.append('file', file);
  const endpoint = `/canvas-chat/consultation/documents?project_id=${encodeURIComponent(projectId)}`;
  onProgress?.({stage: 'uploading'});
  const document = await writeConsultationDocument<ConsultationDocument>(endpoint, {body}, signal);
  onProgress?.({stage: 'ready', document});
  return document;
}

/** Bound upload/import through JSON body completion; an open body must release the composer lock. */
async function requestConsultationDocument<T>(endpoint: string, init: RequestInit, signal?: AbortSignal, timeoutMs = 180_000): Promise<T> {
  const method = init.method ?? 'POST';
  const controller = new AbortController();
  let rejectAborted!: (error: Error) => void;
  const aborted = new Promise<never>((_, reject) => { rejectAborted = reject; });
  const abort = () => {
    controller.abort();
    rejectAborted(new ConsultationRequestError('reference_prepare_failed', 408));
  };
  if (signal?.aborted) abort();
  else signal?.addEventListener('abort', abort, {once: true});
  const timeout = setTimeout(abort, timeoutMs);
  try {
    const request = async () => {
      if (controller.signal.aborted) throw new ConsultationRequestError('reference_prepare_failed', 408);
      const response = await authFetch(getLocalGcpUploadProxyEndpoint(endpoint, method) || `${resolveBackendOrigin()}/api${endpoint}`, {
        ...init, method, signal: controller.signal, timeout: timeoutMs, retries: method === 'GET' ? 2 : 0,
      });
      return readEnvelope<T>(response);
    };
    // authFetch's header timeout ends when fetch resolves. Race through body
    // completion as well, even if a transport drops its signal listener then.
    return await Promise.race([request(), aborted]);
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

const writeConsultationDocument = requestConsultationDocument;

/** 只传资产身份；原文件地址、项目权限及素材访问权由服务端解析验证。 */
export async function importConsultationAsset(projectId: string, assetId: string, signal?: AbortSignal): Promise<ConsultationDocument & { source_asset_id: string }> {
  const endpoint = `/canvas-chat/consultation/documents/from-asset?project_id=${encodeURIComponent(projectId)}`;
  return writeConsultationDocument(endpoint, {
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ asset_id: assetId }),
  }, signal);
}

export function importConsultationLibrary(projectId: string, source: ConsultationLibrarySource,
  maxDocuments: number, signal?: AbortSignal): Promise<{documents: ConsultationDocument[]}> {
  return writeConsultationDocument(`/canvas-chat/consultation/documents/from-library?project_id=${encodeURIComponent(projectId)}`, {
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({...source, max_documents: maxDocuments}),
  }, signal);
}

export async function listConsultationDocuments(projectId: string, offset = 0, signal?: AbortSignal): Promise<{
  documents: ConsultationDocument[];
  next_offset: number | null;
}> {
  const query = new URLSearchParams({ project_id: projectId, offset: String(offset), limit: '50' });
  const response = await authFetch(`${resolveBackendOrigin()}${PREFIX}/documents?${query}`, { signal });
  return readEnvelope(response);
}

export async function getReferenceExploration(scope: import('@/types/consultation').ReferenceExplorationScope, signal?: AbortSignal): Promise<import('@/types/consultation').ReferenceExploration> {
  const query = new URLSearchParams({...scope, version: String(scope.version)});
  const response = await authFetch(`${resolveBackendOrigin()}${PREFIX}/exploration?${query}`, { signal });
  return readEnvelope(response);
}

export async function saveReferenceStyles(scope: import('@/types/consultation').ReferenceExplorationScope,
  candidateIds: string[], signal?: AbortSignal): Promise<import('@/types/custom-style').BatchCreateCustomStylesResult> {
  return readEnvelope(await authFetch(`${resolveBackendOrigin()}${PREFIX}/exploration/saved-styles`, {
    method: 'POST', headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({...scope, candidate_ids: candidateIds}), signal,
  }));
}

export async function refreshReferenceExploration(scope: import('@/types/consultation').ReferenceExplorationScope,
  input: {section_index: number; candidate_ids: string[]; request_id: string}, signal?: AbortSignal): Promise<{
    exploration: import('@/types/consultation').ReferenceExploration; added_count: number; has_more: boolean;
  }> {
  return readEnvelope(await authFetch(`${resolveBackendOrigin()}${PREFIX}/exploration/refresh`, {
    method: 'POST', headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({...scope, ...input}), signal,
  }));
}
export async function getConsultationDocument(projectId: string, documentId: string, signal?: AbortSignal): Promise<ConsultationDocument> {
  const query = new URLSearchParams({project_id: projectId});
  return requestConsultationDocument(`/canvas-chat/consultation/documents/${encodeURIComponent(documentId)}?${query}`, {method: 'GET'}, signal);
}

export async function materializeConsultationDocument(projectId: string, documentId: string, signal?: AbortSignal): Promise<import('@/types/chat').AttachedNodeRef> {
  const query = new URLSearchParams({project_id: projectId});
  return requestConsultationDocument(`/canvas-chat/consultation/documents/${encodeURIComponent(documentId)}/attachment?${query}`, {method: 'POST'}, signal);
}
